import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Pages
import LoginPage from "@/pages/LoginPage";
import AdminCardListPage from "@/pages/AdminCardListPage";
import DashboardPage from "@/pages/DashboardPage";
import ModelDetailPage from "@/pages/ModelDetailPage";
import PublicGuidePage from "@/pages/PublicGuidePage";
import PublicCardListPage from "@/pages/PublicCardListPage";
import PublicCardGuidesPage from "@/pages/PublicCardGuidesPage";

const API = process.env.REACT_APP_BACKEND_URL;

// Keep-alive system to prevent server sleep
const useKeepAlive = () => {
  useEffect(() => {
    const pingServer = async () => {
      try {
        await fetch(`${API}/api/health`, { method: 'GET' });
      } catch (e) {
        // Silent fail - just keeping server awake
      }
    };
    
    // Ping immediately on load
    pingServer();
    
    // Ping every 4 minutes to keep server active (before 5-min timeout)
    const interval = setInterval(pingServer, 4 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);
};

// Theme detection
const ThemeProvider = ({ children }) => {
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const updateTheme = (e) => {
      document.documentElement.classList.toggle('dark', e.matches);
    };
    updateTheme(mediaQuery);
    mediaQuery.addEventListener('change', updateTheme);
    return () => mediaQuery.removeEventListener('change', updateTheme);
  }, []);
  return children;
};

// Auth context
const useAuth = () => {
  const [token, setToken] = useState(() => localStorage.getItem('guideflow_token'));
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('guideflow_user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [loading, setLoading] = useState(true);

  const login = (newToken, userData) => {
    localStorage.setItem('guideflow_token', newToken);
    localStorage.setItem('guideflow_user', JSON.stringify(userData));
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('guideflow_token');
    localStorage.removeItem('guideflow_user');
    localStorage.removeItem('guideflow_showLogs');
    localStorage.removeItem('guideflow_showUsers');
    setToken(null);
    setUser(null);
  };

  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('guideflow_token');
      if (!storedToken) {
        setLoading(false);
        return;
      }
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${storedToken}` }
        });
        if (res.ok) {
          const userData = await res.json();
          localStorage.setItem('guideflow_user', JSON.stringify(userData));
          setUser(userData);
        } else {
          logout();
        }
      } catch (e) {
        console.error('Auth check failed:', e);
        // Don't logout on network error, keep cached user
        if (!user) {
          logout();
        }
      }
      setLoading(false);
    };
    checkAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { token, user, login, logout, loading, setUser };
};

// Protected route wrapper
const ProtectedRoute = ({ children, auth }) => {
  if (auth.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!auth.token) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

function App() {
  const auth = useAuth();
  
  // Keep server alive
  useKeepAlive();

  return (
    <ThemeProvider>
      <BrowserRouter>
        <Routes>
          {/* Public guide page */}
          <Route path="/g/:slug" element={<PublicGuidePage />} />
          
          {/* Auth routes */}
          <Route 
            path="/login" 
            element={
              auth.token ? <Navigate to="/admin" replace /> : <LoginPage auth={auth} />
            } 
          />
          
          {/* Admin routes */}
          <Route 
            path="/admin" 
            element={
              <ProtectedRoute auth={auth}>
                <AdminCardListPage auth={auth} />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/cards/:cardId" 
            element={
              <ProtectedRoute auth={auth}>
                <DashboardPage auth={auth} />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/models/:modelId" 
            element={
              <ProtectedRoute auth={auth}>
                <ModelDetailPage auth={auth} />
              </ProtectedRoute>
            } 
          />
          
          {/* Public home - Redirect to login */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          
          {/* Public card guides page */}
          <Route path="/c/:slug" element={<PublicCardGuidesPage />} />
          
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </ThemeProvider>
  );
}

export default App;
